"use client";

import { useState, useEffect, useSyncExternalStore } from "react";
import { isAuthenticated } from "@/lib/auth";
import { useAuth } from "@/context/AuthContext";
import { captureCouponFromUrl, getCoupon, resolveCouponForPlanType, setPlan, setResolvedCoupon, subscribeToCouponStore } from "@/lib/coupon";
import { ApiPost } from "@/lib/api";
import { getButtonState } from "@/lib/pricing/getButtonState";
import { ROUTES } from "@/lib/routes";
import posthog from "posthog-js";
import { isFeatureEnabled } from "@/lib/feature-flags";
import { CircleCheck, Loader2, Star } from "lucide-react";
import { toast } from "sonner";

const STANDARD_KEYWORDS = ["standard"];
interface Plan {
  id: string;
  name: string;
  description?: string;
  price: number;
  type: "monthly" | "yearly";
  features: string[];
  price_id: string | null;
  original_price?: number;
  discount_percent?: number;
  lookup_key?: string;
}

interface Props {
  planType: "monthly" | "yearly";
}

interface ConfirmDialogState {
  open: boolean;
  planId: string | null;
  planName: string;
  planPrice: number;
  proratedAmount?: number;
  currency?: string;
  nextPaymentDate?: string;
}

export default function PricingContent({ planType }: Props) {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingPlanId, setLoadingPlanId] = useState<string | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogState>({
    open: false,
    planId: null,
    planName: "",
    planPrice: 0,
  });
  // SSR-safe read of the coupon from localStorage / URL. Server snapshot returns
  // null so the initial client render matches the server HTML; React swaps to
  // the live value after hydration. captureCouponFromUrl() below persists the
  // URL ?coupon= to localStorage and notifies subscribers, triggering a
  // re-render with the captured value.
  const couponParam = useSyncExternalStore(
    subscribeToCouponStore,
    () => getCoupon(),
    () => null,
  );

  useEffect(() => {
    captureCouponFromUrl();
  }, []);

  // Fetch user's active subscription to determine upgrade eligibility.
  // Subscribe to the reactive auth state from AuthContext (NOT the sync
  // isAuthenticated() helper) — on a cold page load AuthContext hydrates the
  // token asynchronously after mount, so a one-shot read here would race and
  // leave userSubscription = null even for paid users.
  const { isAuthenticated: isUserAuthed, token: authToken } = useAuth();
  const [userSubscription, setUserSubscription] = useState<{ plan_id: string; plan_name?: string } | null>(null);

  // Authenticated paid users (Standard/Pro/Agency) ignore URL coupons — run normal flow
  const userHasPaidPlan = userSubscription !== null;
  const effectiveCoupon = userHasPaidPlan ? null : couponParam;

  useEffect(() => {
    if (!isUserAuthed) {
      // Auth hydrated to logged-out (e.g., logout in another tab): drop any
      // stale subscription we cached so the UI reflects reality.
      setUserSubscription(null);
      return;
    }

    let cancelled = false;
    const fetchSubscription = async () => {
      try {
        const res = await fetch(`/api/subscriptions`, {
          cache: "no-store",
          headers: {
            ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
          },
        });
        if (cancelled) return;
        if (!res.ok) {
          console.error('Failed to fetch subscription:', res.status);
          return;
        }
        const data = await res.json();
        if (cancelled) return;
        if (data.error) {
          console.error('Server error:', data.error);
          return;
        }
        if (data.subscription) {
          setUserSubscription({
            plan_id: data.subscription.plan_id,
            plan_name: data.subscription.plan_name,
          });
        }
      } catch (err) {
        if (!cancelled) console.error("Failed to fetch subscription:", err);
      }
    };

    fetchSubscription();
    return () => {
      cancelled = true;
    };
  }, [isUserAuthed, authToken]);

  useEffect(() => {
    let cancelled = false;
    let activeController: AbortController | null = null;

    const fetchPlans = async () => {
      activeController?.abort();
      const controller = new AbortController();
      activeController = controller;
      try {
        const url = effectiveCoupon ? `/api/plans?coupon=${encodeURIComponent(effectiveCoupon)}` : "/api/plans";
        const res = await fetch(url, {
          cache: "no-store",
          signal: controller.signal,
        });
        if (!res.ok || cancelled || controller.signal.aborted) return;
        const data = await res.json();
        if (Array.isArray(data) && !cancelled && !controller.signal.aborted) {
          setPlans(data);
        }
      } catch (err) {
        if ((err as Error)?.name === "AbortError") return;
        console.error("Failed to fetch plans:", err);
      }
    };

    fetchPlans();

    return () => {
      cancelled = true;
      activeController?.abort();
    };
  }, [planType, effectiveCoupon]);

  const isStandardPlan = (planName?: string): boolean => {
    if (!planName) return false;
    return STANDARD_KEYWORDS.some(keyword => planName.toLowerCase().includes(keyword));
  };

  // Only hide the free plan once we know the coupon really applied — i.e. the API
  // returned at least one plan with a non-zero discount_percent (which it only does
  // when Stripe validated the code AND the plan type matches the coupon's type).
  // This avoids hiding the free plan for stale/garbage `?coupon=` values.
  const couponIsValid = plans.some((p) => p.discount_percent && p.discount_percent > 0);

  const filteredPlans = plans
    .filter((plan) => plan.type === planType)
    .filter((plan) => !(couponIsValid && plan.price === 0));

  const handleChoosePlan = async (planId: string) => {
    const plan = plans.find((p) => p.id === planId);
    if (!plan) return;

    // Resolve the trigger (sgn-promo / webinar-promo) to the env-mapped Stripe
    // promo code matching this plan's billing period. Returns null when no
    // trigger is active or the env mapping is empty for this plan type.
    // The Stripe code — not the trigger — is what we forward downstream so
    // the backend's /stripe/create-payment-link stays trigger-agnostic.
    const resolvedCoupon = resolveCouponForPlanType(effectiveCoupon, plan.type);

    // Mirror the resolved Stripe code into localStorage. The React app's
    // login.tsx falls back to localStorage when sessionStorage is gone
    // (cross-tab / cross-window after the magic-link email-verify lands in a
    // different tab than the one signup happened in), and it expects a
    // Stripe code in that fallback, not a trigger.
    setResolvedCoupon(resolvedCoupon);

    if (!isAuthenticated()) {
      const params = new URLSearchParams({ plan: plan.lookup_key || plan.id });
      if (resolvedCoupon) {
        params.set("coupon", resolvedCoupon);
      }
      setPlan(plan.lookup_key || plan.id);
      window.location.href = `${ROUTES.signup}?${params.toString()}`;
      return;
    }

    // Authenticated paid users ignore URL coupons — normal flow with backend defaults.
    const coupon = userHasPaidPlan ? null : resolvedCoupon;

    // Check if this is an upgrade (user has Standard plan and is selecting Pro/Agency)
    const isUpgrade = userSubscription && isStandardPlan(userSubscription.plan_name);

    // When flag is off and it's an upgrade, skip proration dialog and call create-payment-link directly
    posthog.capture('upgrade_plan_selected', {
        plan_id: planId,
        plan_name: plan.name,
        source: 'landing_page',
      })

    if (isUpgrade && !isFeatureEnabled("UPGRADE_FLOW")) {
      setLoading(true);
      setLoadingPlanId(planId);
      try {
        const data = await ApiPost<{ url?: string }>("/stripe/create-payment-link", { plan_id: planId, coupon });
        if (data.url) {
          window.location.assign(data.url);
          return;
        }
        toast.error("Checkout failed: missing payment link");
      } catch (err) {
        const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
        const message = detail || (err instanceof Error ? err.message : "Checkout failed");
        toast.error(message);
      } finally {
        setLoading(false);
        setLoadingPlanId(null);
      }
      return;
    }

    if (isUpgrade) {
      // Show confirmation dialog for upgrades - fetch proration preview first
      setConfirmDialog({
        open: true,
        planId,
        planName: plan.name,
        planPrice: plan.price,
        proratedAmount: undefined,
        currency: undefined,
        nextPaymentDate: undefined,
      });

      posthog.capture('upgrade_proration_dialog_viewed', {
        plan_id: planId,
        plan_name: plan.name,
        source: 'landing_page',
      });

      try {
        const preview = await ApiPost<{
          amount_due: number;
          currency: string;
          subtotal: number;
          tax: number | null;
          next_payment_date: string | null;
        }>("/stripe/preview-proration", { new_plan_id: planId });

        setConfirmDialog((prev) => ({
          ...prev,
          proratedAmount: preview.amount_due,
          currency: preview.currency,
          nextPaymentDate: preview.next_payment_date ?? undefined,
        }));
      } catch (err) {
        console.error("Failed to fetch proration preview:", err);
        toast.error("Failed to calculate proration. Please try again.");
        setConfirmDialog((prev) => ({ ...prev, open: false }));
      }
      return;
    }

    setLoading(true);
    setLoadingPlanId(planId);
    try {
      // Regular checkout flow
      const data = await ApiPost<{ url?: string }>("/stripe/create-payment-link", { plan_id: planId, coupon });
      if (data.url) {
        window.location.assign(data.url);
        return;
      }
      toast.error("Checkout failed: missing payment link");
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      const message = detail || (err instanceof Error ? err.message : "Checkout failed");
      toast.error(message);
    } finally {
      setLoading(false);
      setLoadingPlanId(null);
    }
  };

  const handleConfirmUpgrade = async () => {
    const planId = confirmDialog.planId;
    if (!planId) return;

    posthog.capture('upgrade_confirmed', {
      plan_id: planId,
      plan_name: confirmDialog.planName,
      source: 'landing_page',
    });

    setConfirmDialog(prev => ({ ...prev, open: false }));
    setLoading(true);
    setLoadingPlanId(planId);
    try {
      const data = await ApiPost<{ success?: boolean; subscription_id?: string; message?: string; client_secret?: string; hosted_invoice_url?: string }>("/stripe/upgrade-subscription", { new_plan_id: planId });
      if (data.success || data.subscription_id) {
        if (data.hosted_invoice_url) {
          // Same-tab redirect to Stripe hosted payment page (for 3DS/SCA or card
          // auth). window.open(_, "_blank") after `await` is silently blocked by
          // Chrome (the user-gesture token is consumed by the time the promise
          // resolves) — see PR #364. Mirrors the apps/frontend rule.
          window.location.href = data.hosted_invoice_url;
        } else {
          toast.success("Subscription upgraded successfully!");
          window.location.assign(ROUTES.settings);
        }
        return;
      }
      toast.error(data.message || "Upgrade failed: missing response");
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      const message = detail || (err instanceof Error ? err.message : "Checkout failed");
      toast.error(message);
    } finally {
      setLoading(false);
      setLoadingPlanId(null);
    }
  };

  const handleFreePlan = () => {
    window.location.assign(ROUTES.signup);
  };

  return (
    <>
      {/* Upgrade Confirmation Dialog */}
      {confirmDialog.open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          onClick={() => setConfirmDialog(prev => ({ ...prev, open: false }))}
        >
          <div
            className="bg-[#F7F7F7] rounded-3xl p-8 shadow-xl max-w-md w-full mx-4"
            onClick={e => e.stopPropagation()}
            style={{ fontFamily: "'Hanken Grotesk', sans-serif" }}
          >
            <h2
              className="text-xl font-bold text-center mb-4"
              style={{ color: "#1B1C30" }}
            >
              Upgrade to {confirmDialog.planName}?
            </h2>

            <div
              className="bg-white rounded-2xl p-4 mb-6"
              style={{ borderWidth: '2px 0.5px 0.5px 0.5px', borderStyle: 'solid', borderColor: '#1464FF' }}
            >
              <p
                className="text-sm text-center mb-3"
                style={{ color: "#4D4D5C" }}
              >
                You&apos;ll be charged a prorated amount immediately for the remainder of your billing cycle.
                {confirmDialog.nextPaymentDate && (
                  <span className="block mt-1">
                    Your current billing period ends on <strong style={{ color: "#1B1C30" }}>{confirmDialog.nextPaymentDate}</strong>
                  </span>
                )}
              </p>

              {confirmDialog.proratedAmount !== undefined ? (
                <div className="text-center">
                  <span
                    className="text-4xl font-bold"
                    style={{ color: "#1464FF" }}
                  >
                    ${confirmDialog.proratedAmount.toFixed(2)}
                  </span>
                  {confirmDialog.currency && confirmDialog.currency !== "usd" && (
                    <span
                      className="text-lg ml-1"
                      style={{ color: "#4D4D5C" }}
                    >
                      {confirmDialog.currency.toUpperCase()}
                    </span>
                  )}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-2">
                  <Loader2
                    className="w-6 h-6 animate-spin"
                    style={{ color: "#1464FF" }}
                  />
                </div>
              )}
            </div>

            <p
              className="text-xs text-center mb-4"
              style={{ color: "#4D4D5C" }}
            >
              You may be asked to verify your payment details on the next step.
            </p>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  posthog.capture('upgrade_cancelled', {
                    plan_id: confirmDialog.planId,
                    plan_name: confirmDialog.planName,
                    source: 'landing_page',
                  })
                  setConfirmDialog(prev => ({ ...prev, open: false }))
                }}
                className="flex-1 h-12 rounded-[24px] border font-medium transition-all duration-200"
                style={{ borderColor: '#E0EAF4', color: '#4D4D5C', background: '#FFFFFF' }}
              >
                Cancel
              </button>
              {(() => {
                // Disable until the preview proration has resolved — `loading`
                // only flips inside handleConfirmUpgrade, so without this gate
                // a reflexive click while /stripe/preview-proration is in flight
                // commits the upgrade with no prorated amount on screen AND
                // fires two parallel calls. Red-teamed during PR #392 review.
                const previewPending = confirmDialog.proratedAmount === undefined;
                const isDisabled = loading || previewPending;
                return (
                  <button
                    onClick={handleConfirmUpgrade}
                    disabled={isDisabled}
                    className="flex-1 h-12 rounded-[24px] font-medium transition-all duration-200 flex items-center justify-center gap-2"
                    style={{
                      background: isDisabled ? "#E0EAF4" : "#1464FF",
                      color: isDisabled ? "#8B8FA3" : "#FFFFFF",
                      cursor: isDisabled ? "not-allowed" : "pointer",
                    }}
                  >
                    {loading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      "Confirm Upgrade"
                    )}
                  </button>
                );
              })()}
            </div>
          </div>
        </div>
      )}

    <div
      className="flex flex-col md:flex-row items-center justify-center w-full"
      style={{ gap: "16px", zIndex: 2, maxWidth: "1260px" }}
    >
      {filteredPlans.map((plan, index) => {
        const isPopular = index === 1;
        const isProAgency = plan.name.toLowerCase() === "pro agency plan";
        const isFree = plan.price === 0;
        const isYearly = plan.type === "yearly";
        // Yearly plans are stored as the annual total; display the effective
        // per-month figure with a "billed annually" clarifier so visitors can
        // compare against the monthly toggle on the same axis.
        const displayPrice = isYearly && !isFree ? Math.round(plan.price / 12) : plan.price;
        const displayOriginalPrice =
          isYearly && plan.original_price ? Math.round(plan.original_price / 12) : plan.original_price;
        const priceSuffix = isYearly ? "/month, billed annually" : "/month";
        const hasDiscount = Boolean(plan.discount_percent && plan.discount_percent > 0);
        const buttonState = getButtonState(
          plan,
          { loading, isAuthenticated: isAuthenticated(), userSubscription },
          plans,
        );

        return (
          <div
            key={plan.id}
            className="relative flex flex-col items-center"
            style={{
              padding: "24px",
              gap: "48px",
              width: "410px",
              maxWidth: "100%",
              borderRadius: "24px",
              ...(isPopular
                ? {
                    background: "#F7F7F7",
                    boxShadow:
                      "0px 4px 12px rgba(107, 159, 255, 0.16)",
                  }
                : {
                    background: "rgba(255, 255, 255, 0.5)",
                    border: "1px solid #E0EAF4",
                    boxShadow:
                      "0px 4px 12px rgba(75, 70, 92, 0.04)",
                  }),
            }}
          >
            {isProAgency && (
              <div
                className="absolute flex flex-row items-center justify-center"
                style={{
                  width: "85px",
                  height: "30px",
                  top: "-10px",
                  left: "50%",
                  transform: "translateX(-50%)",
                  padding: "8px",
                  gap: "4px",
                  background: "#28C76F",
                  borderRadius: "40px",
                  zIndex: 3,
                }}
              >
                <Star
                  aria-hidden="true"
                  style={{
                    width: "12px",
                    height: "12px",
                    color: "#FFFFFF",
                    strokeWidth: 2,
                  }}
                />
                <span
                  style={{
                    fontFamily: "'Hanken Grotesk', sans-serif",
                    fontStyle: "normal",
                    fontWeight: 600,
                    fontSize: "11px",
                    lineHeight: "14px",
                    color: "#FFFFFF",
                    whiteSpace: "nowrap",
                  }}
                >
                  Best Value
                </span>
              </div>
            )}

            {/* Plan Header */}
            <div
              className="w-full"
              style={{
                paddingBottom: "24px",
                borderBottom: "1px solid #E0EAF4",
                display: "flex",
                flexDirection: "column",
                gap: "16px",
              }}
            >
              <div className="flex items-baseline gap-2">
                <h3
                  style={{
                    fontFamily: "'Hanken Grotesk', sans-serif",
                    fontWeight: 600,
                    fontSize: isPopular ? "24px" : "18px",
                    lineHeight: isPopular ? "47px" : "29px",
                    color: "#1B1C30",
                  }}
                >
                  {plan.name}
                </h3>
                {isPopular && (
                  <span
                    style={{
                      fontFamily: "'Hanken Grotesk', sans-serif",
                      fontWeight: 400,
                      fontSize: "15px",
                      lineHeight: "20px",
                      color: "#4D4D5C",
                    }}
                  >
                    (Most Popular)
                  </span>
                )}
              </div>
              {plan.description && (
                <p
                  style={{
                    fontFamily: "'Hanken Grotesk', sans-serif",
                    fontWeight: 400,
                    fontSize: "15px",
                    lineHeight: "20px",
                    color: "#4D4D5C",
                  }}
                >
                  {plan.description}
                </p>
              )}
              <div
                className="relative flex w-full items-baseline"
                style={{
                  minHeight: "73px",
                }}
              >
                <span
                  className="inline-flex min-w-0 items-baseline whitespace-nowrap"
                >
                  {displayOriginalPrice && displayOriginalPrice > displayPrice && (
                    <span
                      style={{
                        fontFamily: "'Hanken Grotesk', sans-serif",
                        fontSize: "28px",
                        lineHeight: "32px",
                        color: "#9B9B9B",
                        textDecoration: "line-through",
                        marginRight: "8px",
                      }}
                    >
                      ${displayOriginalPrice}
                    </span>
                  )}
                  <span
                    style={{
                      fontFamily: "'Hanken Grotesk', sans-serif",
                      fontWeight: 700,
                      fontSize: "56px",
                      lineHeight: "73px",
                      color: "#1B1C30",
                    }}
                  >
                    ${isFree ? "0" : displayPrice.toLocaleString()}
                  </span>
                  <span
                    style={{
                      fontFamily: "'Hanken Grotesk', sans-serif",
                      fontWeight: 400,
                      fontSize: "15px",
                      lineHeight: "20px",
                      color: "#4D4D5C",
                    }}
                  >
                    {priceSuffix}
                  </span>
                </span>
                {hasDiscount && (
                  <span
                    style={{
                      position: "absolute",
                      top: "6px",
                      right: 0,
                      fontFamily: "'Hanken Grotesk', sans-serif",
                      fontWeight: 600,
                      fontSize: "14px",
                      lineHeight: "18px",
                      color: "#1464FF",
                      background: "#E6F0FF",
                      padding: "4px 8px",
                      borderRadius: "8px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {plan.discount_percent}% off
                  </span>
                )}
              </div>
            </div>

            {/* Features */}
            <div
              className="w-full flex-grow"
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "24px",
              }}
            >
              <h4
                style={{
                  fontFamily: "'Hanken Grotesk', sans-serif",
                  fontWeight: 600,
                  fontSize: "22px",
                  lineHeight: "29px",
                  color: "#1B1C30",
                }}
              >
                What&apos;s included
              </h4>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px",
                }}
              >
                {plan.features?.map((feature, featureIndex) => (
                  <div
                    key={featureIndex}
                    className="flex items-center"
                    style={{ gap: "16px" }}
                  >
                    <CircleCheck
                      className="flex-shrink-0"
                      style={{
                        width: "24px",
                        height: "24px",
                        color: "#4D4D5C",
                        strokeWidth: 1.5,
                      }}
                    />
                    <span
                      style={{
                        fontFamily: "'Hanken Grotesk', sans-serif",
                        fontWeight: 400,
                        fontSize: "15px",
                        lineHeight: "20px",
                        color: "#4D4D5C",
                      }}
                    >
                      {feature}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* CTA Button */}
            <button
              disabled={buttonState.disabled || loading}
              onClick={
                isFree
                  ? handleFreePlan
                  : () => handleChoosePlan(plan.id)
              }
              className="w-full flex items-center justify-center transition-all duration-200"
              style={{
                height: "48px",
                borderRadius: "8px",
                fontFamily: "var(--font-public-sans), 'Public Sans', sans-serif",
                fontWeight: 500,
                fontSize: "17px",
                lineHeight: "22px",
                letterSpacing: "0.43px",
                ...(buttonState.disabled
                  ? { background: "#E0EAF4", color: "#8B8FA3", cursor: "default !important", pointerEvents: "none" }
                  : isPopular
                  ? { background: "#1464FF", color: "#FFFFFF" }
                  : {
                      background:
                        "linear-gradient(0deg, rgba(255, 255, 255, 0.8), rgba(255, 255, 255, 0.8)), #1464FF",
                      color: "#1B1C30",
                    }),
                ...(loading && loadingPlanId === plan.id
                  ? { opacity: 0.7, cursor: "not-allowed" }
                  : {}),
              }}
            >
              {loading && loadingPlanId === plan.id ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : null}
              {buttonState.label}
            </button>
          </div>
        );
      })}
    </div>
    </>
  );
}
